import TimeAgo from 'react-timeago';
import * as moment from 'moment-timezone';
import { TextSize } from '@rsos/base-components/Text';
import { Box } from '@rsos/components/capstone/base';
import { reactTimeAgoFormatter } from '@rsos/utils/timeHelpers';
import {
  TimeBox,
  TimeBoxText,
  TimeAgoText,
  ExtraTime,
} from './locationTime.styles';

interface LocationTimeProps {
  timeStamp: string | number | Date;
  color?: string;
  noDate?: boolean;
  size?: TextSize;
  timeAgoTextSize?: TextSize;
  timeBoxTextSize?: TextSize;
  noPadding?: boolean;
}

const LocationTime = ({
  timeStamp,
  color,
  noDate,
  size = 'small',
  timeAgoTextSize,
  timeBoxTextSize,
  noPadding = false,
}: LocationTimeProps) => {
  // `new Date(null)` and `new Date(0)` both produce the Unix epoch, which
  // is never a valid emergency-call timestamp. Guard here so callers don't
  // need to check before rendering.
  if (!timeStamp) return null;

  const date = new Date(timeStamp); // can be `Invalid Date {}`
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const dd = date.getDate();
  const mm = date.getMonth() + 1;
  const yy = date.getFullYear();
  const tz = moment.tz(moment.tz.guess()).format('z');

  const formattedTimeWithLeadingZero = (time: number) =>
    time > 9 ? time : `0${time}`;
  const isNumberOnly = new RegExp('^[0-9]+$');
  const isFormattedTimeValid = (time: string | number) =>
    isNumberOnly.test(String(time));

  const formattedHours = formattedTimeWithLeadingZero(hours);
  const formattedMinutes = formattedTimeWithLeadingZero(minutes);
  const formattedSeconds = formattedTimeWithLeadingZero(seconds);

  const isNotValidDate = isNaN(date.getTime());
  const isValidTime =
    isFormattedTimeValid(formattedHours) &&
    isFormattedTimeValid(formattedMinutes) &&
    isFormattedTimeValid(formattedSeconds);

  return (
    <Box
      horizontal
      justify="space-between"
      align="center"
      data-name="location-time"
    >
      <TimeBox noPadding={noPadding}>
        <TimeAgoText color={color} size={timeAgoTextSize || size}>
          <TimeAgo
            date={timeStamp}
            formatter={reactTimeAgoFormatter}
            minPeriod={60}
            data-name="location-time-ago"
          />
        </TimeAgoText>
        <TimeBoxText
          {...{ color, size: timeBoxTextSize ? timeBoxTextSize : size }}
          data-name="location-time-hours-minutes"
        >
          {isValidTime ? `${formattedHours}:${formattedMinutes}` : null}
          <ExtraTime
            {...{ color, size: timeBoxTextSize ? timeBoxTextSize : size }}
            data-name="location-time-extra"
          >
            {isValidTime ? `:${formattedSeconds} ${tz}` : null}
            {noDate || isNotValidDate ? null : (
              <>
                , {mm}.{dd}.{yy}
              </>
            )}
          </ExtraTime>
        </TimeBoxText>
      </TimeBox>
    </Box>
  );
};

export default LocationTime;
